import type { KkmJournalEvent } from '@/shared/api';

import { normalizeKkmPayloadKey } from '@/shared/api';

const VOLATILE_SNAPSHOT_KEYS = new Set([
  'command',
  'currentdate',
  'currentdatetime',
  'currenttime',
  'date',
  'datetime',
  'datetimekkt',
  'idcommand',
  'serverdate',
  'serverdatetime',
  'servertime',
  'time',
]);

export interface KkmJournalNoiseFilter {
  shouldRecord(event: KkmJournalEvent): boolean;
}

/**
 * Дедуплицируется только фоновый диагностический опрос: он повторяет одни и те же
 * команды класса `health` бесконечно. Решающие чтения идут теми же командами, но без
 * пометки — их фильтр не трогает, иначе из журнала исчезает основание для решения о смене.
 *
 * Первое наблюдение по ключу всегда попадает в журнал: baseline — это единственная
 * запись, относительно которой видно последующее изменение. Дальше молчание держится,
 * пока стабильная часть ответа не изменилась либо не пришёл сбой.
 */
export function createKkmJournalNoiseFilter(): KkmJournalNoiseFilter {
  const snapshots = new Map<string, string>();

  return {
    shouldRecord(event): boolean {
      if (event.diagnostic !== true) {
        return true;
      }

      if (event.phase === 'request') {
        return false;
      }

      if (event.phase !== 'response' || event.status !== 0) {
        return true;
      }

      const key = `${event.command}:${event.deviceKind}:${event.idDevice ?? ''}`;
      const snapshot = JSON.stringify(stableSnapshot(event.payload));
      const previous = snapshots.get(key);
      snapshots.set(key, snapshot);

      return previous !== snapshot;
    },
  };
}

function stableSnapshot(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map(stableSnapshot);
  }

  if (typeof value !== 'object' || value === null) {
    return value;
  }

  return Object.fromEntries(
    Object.entries(value)
      .filter(([key]) => !VOLATILE_SNAPSHOT_KEYS.has(normalizeKkmPayloadKey(key)))
      .sort(([left], [right]) => left.localeCompare(right))
      .map(([key, item]) => [key, stableSnapshot(item)]),
  );
}
